import React, { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useClinicSubscription } from "../features/subscriptions/hooks/useClinicSubscription"
import useClinicalQrTools from "../features/clinical-history/hooks/useClinicalQrTools"
import useClinicalHistoryHub from "../features/clinical-history/hooks/useClinicalHistoryHub"
import useClinicalHistoryActions from "../features/clinical-history/hooks/useClinicalHistoryActions"
import {
  getInitialForm,
  getInitialEndoForm,
  getInitialPerioForm,
  getInitialDiagnosisForm,
  getInitialSurgeryForm,
  getInitialOrthoForm,
  getInitialPediatricForm,
  calculateAgeFromBirthdate,
  mapEndoInstanceToForm,
  mapPerioInstanceToForm,
  mapSurgeryInstanceToForm,
  mapOrthoInstanceToForm,
  mapPediatricInstanceToForm,
  mapDiagnosisInstanceToForm,
} from "../features/clinical-history/components/ClinicalHistoryHelpers"
import useClinicalTemplateModal from "../features/clinical-history/hooks/useClinicalTemplateModal"
import useDiagnosisInitialModal from "../features/clinical-history/hooks/useDiagnosisInitialModal"
import useClinicalHistoryLauncher from "../features/clinical-history/hooks/useClinicalHistoryLauncher"
import ClinicalHistoryEntryPage from "./ClinicalHistoryEntryPage"
import ClinicalHistoryPatientPage from "./ClinicalHistoryPatientPage"
import ClinicalHistoryBaseEditorPage from "./ClinicalHistoryBaseEditorPage"
import ClinicalHistorySpecialtyCasesPage from "./ClinicalHistorySpecialtyCasesPage"
import ClinicalHistorySpecialtyCasePage from "./ClinicalHistorySpecialtyCasePage"
import ClinicalHistorySpecialtyEvolutionEditorPage from "./ClinicalHistorySpecialtyEvolutionEditorPage"

export default function ClinicalHistoryPage() {
  const navigate = useNavigate()
  const location = useLocation()

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  const patientId = queryParams.get("patientId") || ""
  const clinicIdFromUrl = queryParams.get("clinicId") || ""

  const currentView = queryParams.get("view") || ""
  const isBaseEditorView = currentView === "BASE"

  const specialtyType = queryParams.get("specialtyType") || ""
  const isSpecialtyView = currentView === "SPECIALTY"
  const isEvolutionEditorView = currentView === "EVOLUTION"

  const caseId = queryParams.get("caseId") || ""
  const isSpecialtyCaseView = currentView === "CASE"

  const readStorageClinicId = () => {
    const stored = localStorage.getItem("odontoflow_clinic_id") || ""
    return stored && stored !== "ALL" ? stored : ""
  }

  const [clinicId, setClinicId] = useState(() => {
    return clinicIdFromUrl || readStorageClinicId()
  })

  const { subscription } = useClinicSubscription(clinicId)
  const isReadOnly = subscription?.access?.isReadOnly || false
  const readOnlyReason =
    subscription?.access?.reason || "La clínica está en modo solo lectura."

  const [form, setForm] = useState(getInitialForm())
  const [formBaseline, setFormBaseline] = useState(getInitialForm())

  const age = useMemo(
    () => calculateAgeFromBirthdate(form.birthDate),
    [form.birthDate]
  )

  const patientDisplayName =
    `${form.firstName || ""} ${form.lastName || ""}`.trim() || "Paciente"

  const formSnapshot = useMemo(() => JSON.stringify(form), [form])
  const formBaselineSnapshot = useMemo(() => JSON.stringify(formBaseline), [formBaseline])
  const isFormDirty = Boolean(patientId) && formSnapshot !== formBaselineSnapshot

  const chiefComplaintOptions = [
    { value: "PAIN", label: "Dolor dental" },
    { value: "CARIES", label: "Caries" },
    { value: "GINGIVITIS", label: "Gingivitis" },
    { value: "ORTHODONTICS", label: "Ortodoncia" },
    { value: "ESTHETICS", label: "Estética" },
    { value: "CLEANING", label: "Limpieza" },
    { value: "FRACTURE", label: "Fractura dental" },
    { value: "EXTRACTION", label: "Extracción dental" },
    { value: "OTHER", label: "Otro" },
  ]

  const habitOptions = [
    { value: "THUMB_SUCKING", label: "Succión del pulgar" },
    { value: "PACIFIER", label: "Uso prolongado de chupón" },
    { value: "NAIL_BITING", label: "Onicofagia" },
    { value: "BITE_OBJECTS", label: "Morder objetos/lápices" },
    { value: "MOUTH_BREATHING", label: "Respiración bucal" },
    { value: "BRUXISM", label: "Bruxismo" },
    { value: "SLEEP_POSITION", label: "Hábito postural al dormir" },
    { value: "OTHER", label: "Otro" },
  ]

  const tmjOptions = [
    { value: "CLICKING", label: "Chasquidos o ruidos" },
    { value: "PAIN_OPENING", label: "Dolor al abrir la boca" },
    { value: "MUSCULAR_PAIN", label: "Fatiga o dolor muscular" },
    { value: "LIMITED_OPENING", label: "Dificultad para abrir la boca" },
    { value: "DEVIATION", label: "Desviación al abrir/cerrar" },
  ]

  const medicalOptions = [
    { value: "DIABETES", label: "Diabetes" },
    { value: "HYPERTENSION", label: "Hipertensión" },
    { value: "CARDIOPATHY", label: "Cardiopatía" },
    { value: "PREGNANCY", label: "Embarazo" },
    { value: "ALLERGIES", label: "Alergias" },
    { value: "SYSTEMIC_DISEASE", label: "Enfermedad sistémica" },
    { value: "DRUG_USE", label: "Consumo de drogas" },
    { value: "SMOKING", label: "Tabaquismo" },
    { value: "ALCOHOL", label: "Alcohol" },
    { value: "MEDICATION", label: "Toma medicamento" },
  ]

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  useEffect(() => {
    const syncClinicId = () => {
      const next = clinicIdFromUrl || readStorageClinicId()
      setClinicId((prev) => (prev === next ? prev : next))
    }

    syncClinicId()

    const interval = window.setInterval(syncClinicId, 400)
    const handleVisibility = () => syncClinicId()

    window.addEventListener("focus", syncClinicId)
    window.addEventListener("storage", syncClinicId)
    document.addEventListener("visibilitychange", handleVisibility)

    return () => {
      window.clearInterval(interval)
      window.removeEventListener("focus", syncClinicId)
      window.removeEventListener("storage", syncClinicId)
      document.removeEventListener("visibilitychange", handleVisibility)
    }
  }, [clinicIdFromUrl])

  const {
    hub,
    reloadHub: loadHub,
    hubFormHistoryItems,
    hubTimelineItems,
    hubSpecialtyLauncherItems,
    hubFormHistoryCount,
    hubTimelineCount,
    hubSpecialtyCount,
    clinicalHubCount,
    baseFormSectionCount,
  } = useClinicalHistoryHub({
    patientId,
    clinicId,
    currentBirthDate: form.birthDate,
    setForm,
    setFormBaseline,
  })

  const {
    launcherQuery,
    setLauncherQuery,
    launcherLoading,
    launcherPatients,
    showQuickCreate,
    setShowQuickCreate,
    quickCreateForm,
    setQuickCreateForm,
    creatingPatient,
    resetQuickCreateForm,
    openPatientHistory,
    handleCreatePatient,
  } = useClinicalHistoryLauncher({
    clinicId,
    patientId,
    isReadOnly,
    readOnlyReason,
    navigate,
  })

  const {
    qrLoading,
    qrGenerating,
    qrInfo,
    qrImageUrl,
    qrImageLoading,
    handleGenerateQrLink,
    handleCopyQrLink,
    handleOpenQrLink,
    handleDownloadQr,
    handlePrintQrPoster,
  } = useClinicalQrTools({
    clinicId,
    clinicName: hub?.patient?.clinicName || "Clínica",
    isReadOnly,
    readOnlyReason,
  })

  const endoModal = useClinicalTemplateModal({
    templateCode: "ENDO",
    clinicId,
    patientId,
    isReadOnly,
    readOnlyReason,
    getInitialForm: getInitialEndoForm,
    mapInstanceToForm: mapEndoInstanceToForm,
    validateForm: (currentForm) => {
      if (!currentForm.tooth.trim()) return "Indica la pieza dental."
      return ""
    },
    buildTitle: (currentForm) =>
      `Historia de Endodoncia${currentForm.tooth ? ` · Pieza ${currentForm.tooth}` : ""}`,
    buildSummary: (currentForm) =>
      [
        currentForm.tooth ? `Pieza ${currentForm.tooth}` : "",
        currentForm.pulpDiagnosis || "",
        currentForm.apicalDiagnosis || "",
      ]
        .filter(Boolean)
        .join(" · "),
    successMessage: "Historia de Endodoncia guardada correctamente.",
    onSaved: async () => {
      await loadHub()
    },
  })

  const perioModal = useClinicalTemplateModal({
    templateCode: "PERIO",
    clinicId,
    patientId,
    isReadOnly,
    readOnlyReason,
    getInitialForm: getInitialPerioForm,
    mapInstanceToForm: mapPerioInstanceToForm,
    validateForm: (currentForm) => {
      if (!currentForm.chiefComplaint.trim()) {
        return "Escribe el motivo periodontal de consulta."
      }
      return ""
    },
    buildTitle: () => "Historia de Periodoncia",
    buildSummary: (currentForm) =>
      [
        currentForm.periodontalDiagnosis || "",
        currentForm.riskLevel ? `Riesgo ${currentForm.riskLevel}` : "",
        currentForm.maintenanceInterval
          ? `Mantenimiento ${currentForm.maintenanceInterval}`
          : "",
      ]
        .filter(Boolean)
        .join(" · "),
    successMessage: "Historia de Periodoncia guardada correctamente.",
    onSaved: async () => {
      await loadHub()
    },
  })

  const surgeryModal = useClinicalTemplateModal({
    templateCode: "SURGERY",
    clinicId,
    patientId,
    isReadOnly,
    readOnlyReason,
    getInitialForm: getInitialSurgeryForm,
    mapInstanceToForm: mapSurgeryInstanceToForm,
    validateForm: (currentForm) => {
      if (!currentForm.surgicalDiagnosis.trim() && !currentForm.procedureType.trim()) {
        return "Escribe el diagnóstico quirúrgico o el procedimiento."
      }
      return ""
    },
    buildTitle: (currentForm) =>
      `Historia de Cirugía${currentForm.tooth ? ` · Pieza ${currentForm.tooth}` : ""}`,
    buildSummary: (currentForm) =>
      [
        currentForm.procedureType || "",
        currentForm.tooth ? `Pieza ${currentForm.tooth}` : "",
        currentForm.surgicalArea || "",
      ]
        .filter(Boolean)
        .join(" · "),
    successMessage: "Historia de Cirugía guardada correctamente.",
    onSaved: async () => {
      await loadHub()
    },
  })

  const orthoModal = useClinicalTemplateModal({
    templateCode: "ORTHO",
    clinicId,
    patientId,
    isReadOnly,
    readOnlyReason,
    getInitialForm: getInitialOrthoForm,
    mapInstanceToForm: mapOrthoInstanceToForm,
    validateForm: (currentForm) => {
      if (!currentForm.chiefComplaint.trim() && !currentForm.treatmentObjectives.trim()) {
        return "Escribe el motivo ortodóntico o los objetivos del tratamiento."
      }
      return ""
    },
    buildTitle: () => "Historia de Ortodoncia",
    buildSummary: (currentForm) =>
      [
        currentForm.skeletalClass || "",
        currentForm.molarClassRight ? `Molar der ${currentForm.molarClassRight}` : "",
        currentForm.molarClassLeft ? `Molar izq ${currentForm.molarClassLeft}` : "",
        currentForm.treatmentObjectives || "",
      ]
        .filter(Boolean)
        .join(" · "),
    successMessage: "Historia de Ortodoncia guardada correctamente.",
    onSaved: async () => {
      await loadHub()
    },
  })

  const pediatricModal = useClinicalTemplateModal({
    templateCode: "PEDIATRIC",
    clinicId,
    patientId,
    isReadOnly,
    readOnlyReason,
    getInitialForm: getInitialPediatricForm,
    mapInstanceToForm: mapPediatricInstanceToForm,
    validateForm: (currentForm) => {
      if (!currentForm.chiefComplaint.trim() && !currentForm.treatmentPlan.trim()) {
        return "Escribe el motivo pediátrico o el plan de tratamiento."
      }
      return ""
    },
    buildTitle: () => "Historia de Odontopediatría",
    buildSummary: (currentForm) =>
      [
        currentForm.companionRelation
          ? `Acompañante ${currentForm.companionRelation}`
          : "",
        currentForm.cariesRisk ? `Riesgo ${currentForm.cariesRisk}` : "",
        currentForm.behaviorDuringConsultation || "",
        currentForm.treatmentPlan || "",
      ]
        .filter(Boolean)
        .join(" · "),
    successMessage: "Historia de Odontopediatría guardada correctamente.",
    onSaved: async () => {
      await loadHub()
    },
  })

  const handleOpenEndo = endoModal.openModal

  const handleOpenPerio = perioModal.openModal

  const handleOpenSurgery = surgeryModal.openModal

  const handleOpenOrtho = orthoModal.openModal

  const handleOpenPediatric = pediatricModal.openModal

  const pushDiagnosisPrefillToPlans = (savedInstance, sourceForm) => {
    if (!sourceForm) return
    if (!patientId || !clinicId) return

    const mappedPriority =
      sourceForm.priority === "HIGH"
        ? "ALTA"
        : sourceForm.priority === "LOW"
          ? "BAJA"
          : "MEDIA"

    const notes = [
      "Diagnóstico inicial enviado desde historia clínica.",
      `Tipo: ${sourceForm.diagnosisType || "-"}`,
      `Diagnóstico principal: ${sourceForm.mainDiagnosis || "-"}`,
      `Diagnóstico secundario: ${sourceForm.secondaryDiagnosis || "-"}`,
      `Área afectada: ${sourceForm.affectedArea || "-"}`,
      `Pieza: ${sourceForm.tooth || "-"}`,
      `Superficie: ${sourceForm.surface || "-"}`,
      `Severidad: ${sourceForm.severity || "-"}`,
      sourceForm.notes ? `Notas: ${sourceForm.notes}` : "",
    ]
      .filter(Boolean)
      .join("\n")

    localStorage.setItem(
      "odontoflow_plan_prefill_diagnosis",
      JSON.stringify({
        source: "INITIAL_DIAGNOSIS",
        patientId,
        clinicId,
        formInstanceId: savedInstance?.id || null,
        title: `Plan derivado de diagnóstico${sourceForm.tooth ? ` · Pieza ${sourceForm.tooth}` : ""}`,
        suggestedTreatment:
          sourceForm.recommendedTreatment ||
          sourceForm.mainDiagnosis ||
          "",
        estimatedPrice: "",
        priority: mappedPriority,
        tooth: sourceForm.tooth || "",
        surface: sourceForm.surface || "ALL",
        diagnosisType: sourceForm.diagnosisType || "PRELIMINARY",
        mainDiagnosis: sourceForm.mainDiagnosis || "",
        secondaryDiagnosis: sourceForm.secondaryDiagnosis || "",
        severity: sourceForm.severity || "MEDIUM",
        notes,
        createdAt: new Date().toISOString(),
      })
    )

    navigate(`/patients/${patientId}?clinicId=${clinicId}&tab=PLANS`)
  }

  const diagnosisModal = useDiagnosisInitialModal({
    clinicId,
    patientId,
    isReadOnly,
    readOnlyReason,
    getInitialForm: getInitialDiagnosisForm,
    mapInstanceToForm: mapDiagnosisInstanceToForm,
    onSaved: async () => {
      await loadHub()
    },
    onSendToTreatmentPlan: (savedInstance, currentForm) => {
      pushDiagnosisPrefillToPlans(savedInstance, currentForm)
    },
  })

  const handleOpenDiagnosis = diagnosisModal.openModal

  const {
    saving,
    qrReviewModalOpen,
    setQrReviewModalOpen,
    markingQrReviewed,
    dismissedQrReviewId,
    persistClinicalHistory,
    handleSaveDraft,
    handleOpenOdontogram,
    handleOpenPatientsProfile,
    handleBackToLauncher,
    handleOpenUpgrade,
    handleUseQrIntakeDataAndMarkReviewed,
    handleMarkQrReviewed,
    handleHubHistoryAction,
    handleHubHistorySecondaryAction,
  } = useClinicalHistoryActions({
    patientId,
    clinicId,
    form,
    isFormDirty,
    isReadOnly,
    readOnlyReason,
    navigate,
    hub,
    setForm,
    onLoadHub: loadHub,
    onOpenEndo: handleOpenEndo,
    onOpenPerio: handleOpenPerio,
    onOpenSurgery: handleOpenSurgery,
    onOpenOrtho: handleOpenOrtho,
    onOpenPediatric: handleOpenPediatric,
    onOpenDiagnosis: handleOpenDiagnosis,
  })

  if (!patientId) {
    return (
      <ClinicalHistoryEntryPage
        clinicId={clinicId}
        isReadOnly={isReadOnly}
        readOnlyReason={readOnlyReason}
        launcherQuery={launcherQuery}
        setLauncherQuery={setLauncherQuery}
        launcherLoading={launcherLoading}
        launcherPatients={launcherPatients}
        showQuickCreate={showQuickCreate}
        setShowQuickCreate={setShowQuickCreate}
        quickCreateForm={quickCreateForm}
        setQuickCreateForm={setQuickCreateForm}
        resetQuickCreateForm={resetQuickCreateForm}
        creatingPatient={creatingPatient}
        onCreatePatient={handleCreatePatient}
        onOpenPatientHistory={openPatientHistory}
        qrLoading={qrLoading}
        qrGenerating={qrGenerating}
        qrInfo={qrInfo}
        qrImageUrl={qrImageUrl}
        qrImageLoading={qrImageLoading}
        onGenerateQrLink={handleGenerateQrLink}
        onCopyQrLink={handleCopyQrLink}
        onOpenQrLink={handleOpenQrLink}
        onDownloadQr={handleDownloadQr}
        onPrintQrPoster={handlePrintQrPoster}
        onOpenUpgrade={handleOpenUpgrade}
      />
    )
  }

  const handleOpenBaseEditor = () => {
    const params = new URLSearchParams()
    params.set("patientId", patientId)
    if (clinicId) params.set("clinicId", clinicId)
    params.set("view", "BASE")
    navigate(`/clinical-history?${params.toString()}`)
  }

  const handleBackToPatientSummary = () => {
    const params = new URLSearchParams()
    params.set("patientId", patientId)
    if (clinicId) params.set("clinicId", clinicId)
    navigate(`/clinical-history?${params.toString()}`)
  }

  const handleOpenSpecialtyCases = (type) => {
    const params = new URLSearchParams()
    params.set("patientId", patientId)
    if (clinicId) params.set("clinicId", clinicId)
    params.set("view", "SPECIALTY")
    params.set("specialtyType", type)
    navigate(`/clinical-history?${params.toString()}`)
  }

  const handleOpenSpecialtyCase = (specialtyCase) => {
    const params = new URLSearchParams()
    params.set("patientId", patientId)
    if (clinicId) params.set("clinicId", clinicId)
    params.set("view", "CASE")
    params.set("specialtyType", specialtyCase.specialtyType || specialtyType)
    params.set("caseId", specialtyCase.id)
    navigate(`/clinical-history?${params.toString()}`)
  }

  const handleBackToSpecialtyCases = () => {
    const params = new URLSearchParams()
    params.set("patientId", patientId)
    if (clinicId) params.set("clinicId", clinicId)
    params.set("view", "SPECIALTY")
    params.set("specialtyType", specialtyType)
    navigate(`/clinical-history?${params.toString()}`)
  }

  const handleOpenNewEvolution = () => {
    const params = new URLSearchParams()
    params.set("patientId", patientId)
    if (clinicId) params.set("clinicId", clinicId)
    params.set("view", "EVOLUTION")
    params.set("specialtyType", specialtyType)
    params.set("caseId", caseId)
    navigate(`/clinical-history?${params.toString()}`)
  }

  const handleBackToSpecialtyCase = () => {
    const params = new URLSearchParams()
    params.set("patientId", patientId)
    if (clinicId) params.set("clinicId", clinicId)
    params.set("view", "CASE")
    params.set("specialtyType", specialtyType)
    params.set("caseId", caseId)
    navigate(`/clinical-history?${params.toString()}`)
  }

  if (isEvolutionEditorView && specialtyType && caseId) {
    return (
      <ClinicalHistorySpecialtyEvolutionEditorPage
        clinicId={clinicId}
        patientId={patientId}
        caseId={caseId}
        specialtyType={specialtyType}
        isReadOnly={isReadOnly}
        readOnlyReason={readOnlyReason}
        onBackToCase={handleBackToSpecialtyCase}
      />
    )
  }

  if (isSpecialtyCaseView && specialtyType && caseId) {
    return (
      <ClinicalHistorySpecialtyCasePage
        clinicId={clinicId}
        caseId={caseId}
        specialtyType={specialtyType}
        patientDisplayName={patientDisplayName}
        onBackToCases={handleBackToSpecialtyCases}
        onOpenNewEvolution={handleOpenNewEvolution}
      />
    )
  }

  if (isSpecialtyView && specialtyType) {
    return (
      <ClinicalHistorySpecialtyCasesPage
        clinicId={clinicId}
        patientId={patientId}
        patientDisplayName={patientDisplayName}
        specialtyType={specialtyType}
        onBackToSummary={handleBackToPatientSummary}
        onOpenCase={handleOpenSpecialtyCase}
      />
    )
  }

  if (isBaseEditorView) {
    return (
      <ClinicalHistoryBaseEditorPage
        patientDisplayName={patientDisplayName}
        isFormDirty={isFormDirty}
        isReadOnly={isReadOnly}
        readOnlyReason={readOnlyReason}
        saving={saving}
        onBackToSummary={handleBackToPatientSummary}
        baseFormSectionCount={baseFormSectionCount}
        form={form}
        age={age}
        updateField={updateField}
        chiefComplaintOptions={chiefComplaintOptions}
        habitOptions={habitOptions}
        tmjOptions={tmjOptions}
        medicalOptions={medicalOptions}
        onOpenOdontogram={handleOpenOdontogram}
        onOpenDiagnosis={handleOpenDiagnosis}
        onPersistClinicalHistory={persistClinicalHistory}
        onSaveDraft={handleSaveDraft}
        diagnosisModal={diagnosisModal}
      />
    )
  }

  return (
    <ClinicalHistoryPatientPage
      patientDisplayName={patientDisplayName}
      dismissedQrReviewId={dismissedQrReviewId}
      hub={hub}
      age={age}
      form={form}
      isFormDirty={isFormDirty}
      isReadOnly={isReadOnly}
      readOnlyReason={readOnlyReason}
      saving={saving}
      onBackToLauncher={handleBackToLauncher}
      onOpenPatientsProfile={handleOpenPatientsProfile}
      onOpenBaseEditor={handleOpenBaseEditor}
      onOpenSpecialtyCases={handleOpenSpecialtyCases}
      onOpenOdontogram={handleOpenOdontogram}
      qrLoading={qrLoading}
      qrGenerating={qrGenerating}
      qrInfo={qrInfo}
      qrImageUrl={qrImageUrl}
      qrImageLoading={qrImageLoading}
      onGenerateQrLink={handleGenerateQrLink}
      onCopyQrLink={handleCopyQrLink}
      onOpenQrLink={handleOpenQrLink}
      onDownloadQr={handleDownloadQr}
      onPrintQrPoster={handlePrintQrPoster}
      onOpenUpgrade={handleOpenUpgrade}
      clinicalHubCount={clinicalHubCount}
      hubFormHistoryItems={hubFormHistoryItems}
      hubTimelineItems={hubTimelineItems}
      hubSpecialtyLauncherItems={hubSpecialtyLauncherItems}
      hubFormHistoryCount={hubFormHistoryCount}
      hubTimelineCount={hubTimelineCount}
      hubSpecialtyCount={hubSpecialtyCount}
      onHubHistoryAction={handleHubHistoryAction}
      onHubHistorySecondaryAction={handleHubHistorySecondaryAction}
      endoModal={endoModal}
      perioModal={perioModal}
      surgeryModal={surgeryModal}
      orthoModal={orthoModal}
      pediatricModal={pediatricModal}
      diagnosisModal={diagnosisModal}
      qrReviewModalOpen={qrReviewModalOpen}
      setQrReviewModalOpen={setQrReviewModalOpen}
      markingQrReviewed={markingQrReviewed}
      onUseQrDataAndMarkReviewed={handleUseQrIntakeDataAndMarkReviewed}
      onMarkQrReviewed={handleMarkQrReviewed}
    />
  )
}